// Author - Atul Pandey
// First Created - 17 Jun 2025
// Purpose - To have consistent weekStarting and weekEnding boundary calculations for components in the frontend to match with the backend

// Original Update Trail - /server/utils
//--------------------------------------------
// Created - 15 Sep 2024 - getNearestMonday Function
// Updated - 21 Jan 2025 - week boundary functions for a year - calculateFirstWeek, calculateLastWeek and calculateWeekBoundaries
// Updated - 05 Mar 2025 - refined all the week boundary functions for PT timezone in both local and production env (UTC)
// Updated - 05 Mar 2025 - added function convertToPacificTime
// Updated - 18 Apr 2025 - added function calculatePTDayBoundaries - for daily Performance Logs
// Updated - 01 Sep 2025 - moment timezone offset for user Actions - to honor the user selected dates in their local timezones
// Updated - 14 Nov 2025 - moment timezone offset for User Missions
// Updated - 03 Jan 2026 - bug fix - calculateFirstWeek(date) - Sunday time boundary - 23, 59, 59, instead of subtracting 1ms, it was offsetting the day to the prior day.
// Updated - 05 Jan 2026 - bug fix - routing for calling calculateFirstWeek(date) - only if needed. Not hard routing for all days between 1-7 Jan and calculateLastWeek(date) to not hard route between 24 - 31 Dec

// 01 Sep 2025
import moment from 'moment-timezone'

// 1a - Helper function to get the nearest Monday - created 15 Sep 2024
const getNearestMonday = (date) => {
    const dayOfWeek = date.getDay(); // 0 for Sunday, 1 for Monday, etc.
    const daysSinceMonday = (dayOfWeek === 0) ? 6 : dayOfWeek - 1; // If Sunday, move 6 days back; otherwise, move back to Monday
    const nearestMonday = new Date(date);
    nearestMonday.setDate(date.getDate() - daysSinceMonday);
    return nearestMonday;
};

function getPacificTimeOffset(utcDate) {
    // Create a formatter in Pacific Time
    const formatter = new Intl.DateTimeFormat('en-US', {
        timeZone: 'America/Los_Angeles',
        year: 'numeric',
        month: 'numeric',
        day: 'numeric',
        hour: 'numeric',
        minute: 'numeric',
        second: 'numeric',
        hour12: false
    });
    
    // Format the date in Pacific Time
    const pacificParts = formatter.formatToParts(utcDate);
    
    // Extract components
    const pacificComponents = {};
    pacificParts.forEach(part => {
        if (part.type !== 'literal') {
            pacificComponents[part.type] = parseInt(part.value, 10);
        }
    });
    
    // Create a new Date with Pacific components (in UTC)
    const pacificDate = new Date(Date.UTC(
        pacificComponents.year,
        pacificComponents.month - 1, // JS months are 0-based
        pacificComponents.day,
        pacificComponents.hour,
        pacificComponents.minute,
        pacificComponents.second
    ));
    
    // Calculate the offset
    return utcDate.getTime() - pacificDate.getTime();
}

function calculateWeekBoundaries(date) {
    if (!(date instanceof Date) || isNaN(date.getTime())) {
        throw new Error("Invalid date provided");
    }
    
    // console.log('--------inside calculateWeekBoundaries-------');
    // console.log('date passed to the function:', date);
    
    // Step 1: Get a date object that represents the time in PT
    const ptOffset = getPacificTimeOffset(date);
    const ptTimestamp = date.getTime() - ptOffset;
    const ptDate = new Date(ptTimestamp);
    
    // console.log('PT date equivalent:', ptDate);
    
    // Extract date components once
    const ptYear = ptDate.getUTCFullYear();
    const ptMonth = ptDate.getUTCMonth();
    const ptDay = ptDate.getUTCDate();
    
    // Step 2: Handle special boundary cases

    // 05 Jan 2026 - Fix for routing for first week of Jan
    // -------------------------------
    // Determine FIRST WEEK END (PT)
    // -------------------------------
    const firstDayOfYear = new Date(Date.UTC(ptYear, 0, 1, 0, 0, 0, 0));
    const firstDayOfWeek = firstDayOfYear.getUTCDay(); // 0=Sun, 1=Mon...

    let daysTillSunday;
    if (firstDayOfWeek === 0) {
    // Jan 1 is Sunday → first week ends on NEXT Sunday (Jan 8)
    daysTillSunday = 7;
    } else {
    // Otherwise → same-week Sunday
    daysTillSunday = 7 - firstDayOfWeek;
    }

    // First week ends at THIS PT moment
    const firstWeekEndPT = new Date(
    Date.UTC(ptYear, 0, 1 + daysTillSunday, 23, 59, 59, 999)
    );

    // -------------------------------
    // Route ONLY if still inside first week
    // -------------------------------
    if (ptDate.getTime() <= firstWeekEndPT.getTime()) {
    return calculateFirstWeek(date);
    }

    // -------------------------------
    // Determine LAST WEEK START (PT) - 05 Jan 2026
    // -------------------------------
    const lastDayOfYear = new Date(Date.UTC(ptYear, 11, 31, 0, 0, 0, 0));
    const lastDayOfWeek = lastDayOfYear.getUTCDay();

    let daysToSubtract;
    if (lastDayOfWeek === 1) {
    daysToSubtract = 7;
    } else if (lastDayOfWeek === 0) {
    daysToSubtract = 6;
    } else {
    daysToSubtract = lastDayOfWeek - 1;
    }

    const lastWeekStartPT = new Date(
    Date.UTC(ptYear, 11, 31 - daysToSubtract, 0, 0, 0, 0)
    );

    // -------------------------------
    // Route ONLY if inside last week
    // -------------------------------
    if (ptDate.getTime() >= lastWeekStartPT.getTime()) {
    return calculateLastWeek(date);
    }

    //05 Jan 2026 - Commented this block - wrong routing
   
    // if (ptMonth === 0 && ptDay <= 7) {
    //     // First week of the year (Jan 1-7)
    //     return calculateFirstWeek(date);
    // }
    
    // if (ptMonth === 11 && ptDay >= 24) {
    //     // Last week of the year (Dec 24-31)
    //     return calculateLastWeek(date);
    // }
    
    // Step 3: Calculate regular week boundaries (Monday-Sunday)
    const ptDayOfWeek = ptDate.getUTCDay(); 
    const daysToMonday = ptDayOfWeek === 0 ? 6 : ptDayOfWeek - 1;
    // console.log('Days to Monday:', daysToMonday);
    
    // Calculate the exact date for Monday
    const ptMonthDay = ptDay - daysToMonday;
    
    // Create start of week (Monday 00:00:00.000 PT) with exact precision
    const ptWeekStart = new Date(Date.UTC(ptYear, ptMonth, ptMonthDay, 0, 0, 0, 0));
    // console.log('PT Week Start:', ptWeekStart);
    
    // Create end of week (Sunday 23:59:59.999 PT)
    const ptWeekEnd = new Date(Date.UTC(ptYear, ptMonth, ptMonthDay + 6, 23, 59, 59, 999));
    // console.log('PT Week End:', ptWeekEnd);
    
    // Calculate separate offsets for start and end dates to handle DST changes
    const startOffset = getPacificTimeOffset(new Date(ptWeekStart.getTime()));
    const endOffset = getPacificTimeOffset(new Date(ptWeekEnd.getTime()));
    
    // console.log('Start offset (ms):', startOffset);
    // console.log('End offset (ms):', endOffset);
    
    // Convert to server timezone with the correct offsets
    const weekStarting = new Date(ptWeekStart.getTime() + startOffset);
    // Check if start date is in PDT
    const isStartPDT = startOffset < 28000000; // ~7 hours is PDT, ~8 hours is PST
    // Force exact time with 00:00:00.000 in correct timezone
    weekStarting.setUTCHours(isStartPDT ? 7 : 8, 0, 0, 0);

    const weekEnding = new Date(ptWeekEnd.getTime() + endOffset);
    // Check if end date is in PDT
    const isEndPDT = endOffset < 28000000;
    // Force exact time with 59:59.999 in correct timezone
    weekEnding.setUTCHours(isEndPDT ? 6 : 7, 59, 59, 999);
    
    // console.log('-----Final output from calculateWeekBoundaries----');
    // console.log('weekStarting (server timezone):', weekStarting);
    // console.log('weekEnding (server timezone):', weekEnding);
    // console.log('--------------------------------------------------');
    
    return { weekStarting, weekEnding };
}

function calculateFirstWeek(date) {
    const ptOffset = getPacificTimeOffset(date);
    const ptTimestamp = date.getTime() - ptOffset;
    const ptDate = new Date(ptTimestamp);
    
    // Get year in Pacific Time
    const ptYear = ptDate.getUTCFullYear();
    
    // January 1st 00:00:00.000 PT
    const ptWeekStart = new Date(Date.UTC(ptYear, 0, 1, 0, 0, 0, 0));
    
    // Find the first Sunday of the year in PT
    const firstDayOfYear = new Date(Date.UTC(ptYear, 0, 1, 0, 0, 0, 0));
    const firstDayOfWeek = firstDayOfYear.getUTCDay(); // 0=Sunday, 1=Monday, etc.
    
    let daysTillSunday;
    if (firstDayOfWeek === 0) { // If Jan 1 is Sunday
        daysTillSunday = 7; // Go to the next Sunday (Jan 8)
    } else {
        daysTillSunday = 7 - firstDayOfWeek; // Go to first Sunday
    }
    
    // Set the end date to the first Sunday at 23:59:59.999 PT

    // 03 Jan 2026 - Bug Fix
    const ptWeekEnd = new Date(
        Date.UTC(ptYear, 0, 1 + daysTillSunday, 23, 59, 59, 999)
    );

    // This prior code had a bug - the SundayDate calculated the date to the start of the Sunday and then we shifted it by a ms, making it Saturday end of day.

    // const sundayDate = new Date(Date.UTC(ptYear, 0, 1 + daysTillSunday, 0, 0, 0, 0));
    // const ptWeekEnd = new Date(sundayDate.getTime() - 1); // 23:59:59.999 of the Sunday
    
    // January is always in PST (Standard Time)
    const weekStarting = new Date(ptWeekStart.getTime() + ptOffset);
    weekStarting.setUTCHours(8, 0, 0, 0); // Always 8:00 UTC for PST
    
    const weekEnding = new Date(ptWeekEnd.getTime() + ptOffset);
    weekEnding.setUTCHours(7, 59, 59, 999); // Always 7:59:59.999 UTC for PST

    // console.log('-----Final output from calculateFirstWeek in calculateWeekBoundaries----');
    // console.log('weekStarting (server timezone):', weekStarting);
    // console.log('weekEnding (server timezone):', weekEnding);
    // console.log('--------------------------------------------------');
    
    return { weekStarting, weekEnding };
}

function calculateLastWeek(date) {
    const ptOffset = getPacificTimeOffset(date);
    const ptTimestamp = date.getTime() - ptOffset;
    const ptDate = new Date(ptTimestamp);
    
    // Get year in Pacific Time
    const ptYear = ptDate.getUTCFullYear();
    
    // Last day of year 23:59:59.999 PT
    const nextYearStart = new Date(Date.UTC(ptYear + 1, 0, 1, 0, 0, 0, 0));
    const ptWeekEnd = new Date(nextYearStart.getTime() - 1); // Dec 31 23:59:59.999
    
    // Determine day of week for Dec 31
    const lastDayOfYear = new Date(Date.UTC(ptYear, 11, 31, 0, 0, 0, 0));
    const lastDayOfWeek = lastDayOfYear.getUTCDay(); // 0=Sunday, 1=Monday, etc.
    
    let daysToSubtract;
    if (lastDayOfWeek === 1) { // If Dec 31 is Monday
        daysToSubtract = 7; // Go back to previous Monday (Dec 24)
    } else if (lastDayOfWeek === 0) { // If Dec 31 is Sunday
        daysToSubtract = 6; // Go back to previous Monday (Dec 25)
    } else {
        daysToSubtract = lastDayOfWeek - 1; // Go back to Monday of same week
    }
    
    // Calculate the starting Monday at 00:00:00.000 PT
    const ptWeekStart = new Date(Date.UTC(ptYear, 11, 31 - daysToSubtract, 0, 0, 0, 0));
    
    // December is always in PST (Standard Time)
    const weekStarting = new Date(ptWeekStart.getTime() + ptOffset);
    weekStarting.setUTCHours(8, 0, 0, 0); // Always 8:00 UTC for PST
    
    const weekEnding = new Date(ptWeekEnd.getTime() + ptOffset);
    weekEnding.setUTCHours(7, 59, 59, 999); // Always 7:59:59.999 UTC for PST

    // console.log('-----Final output from calculateLastWeek in calculateWeekBoundaries----');
    // console.log('weekStarting (server timezone):', weekStarting);
    // console.log('weekEnding (server timezone):', weekEnding);
    // console.log('--------------------------------------------------');
    
    return { weekStarting, weekEnding };
}

// 18 Apr 2025 - for daily Performance Logs
function calculatePTDayBoundaries(date) {
    if (!(date instanceof Date) || isNaN(date.getTime())) {
      throw new Error("Invalid date provided");
    }
  
    const ptOffset = getPacificTimeOffset(date);
    const ptTimestamp = date.getTime() - ptOffset;
    const ptDate = new Date(ptTimestamp);
  
    const ptYear = ptDate.getUTCFullYear();
    const ptMonth = ptDate.getUTCMonth();
    const ptDay = ptDate.getUTCDate();
  
    // Create PT start (00:00:00.000)
    const ptDayStart = new Date(Date.UTC(ptYear, ptMonth, ptDay, 0, 0, 0, 0));
    const ptDayEnd = new Date(Date.UTC(ptYear, ptMonth, ptDay, 23, 59, 59, 999));
  
    const startOffset = getPacificTimeOffset(new Date(ptDayStart.getTime()));
    const endOffset = getPacificTimeOffset(new Date(ptDayEnd.getTime()));
  
    const dayStarting = new Date(ptDayStart.getTime() + startOffset);
    dayStarting.setUTCHours(startOffset < 28000000 ? 7 : 8, 0, 0, 0); // PDT vs PST
  
    const dayEnding = new Date(ptDayEnd.getTime() + endOffset);
    dayEnding.setUTCHours(endOffset < 28000000 ? 6 : 7, 59, 59, 999);
  
    return { dayStarting, dayEnding };
}


/**
 * 01 Sep 2025
 * Converts a YYYY-MM-DD string (selected via <input type="date" />)
 * to a UTC ISO string, interpreting the input as midnight in the user's local timezone.
 */
function convertLocalDateToUTCISO(dateStr) {
  if (!dateStr) return null

  const userTimeZone = Intl.DateTimeFormat().resolvedOptions().timeZone
  const localMoment = moment.tz(dateStr, 'YYYY-MM-DD', userTimeZone)

  return localMoment.utc().toISOString()
}

/**
 * 01 Sep 2025
 * Converts a stored UTC ISO string into a YYYY-MM-DD string
 * in the user's local timezone, suitable for <input type="date" />
 */
function formatUTCToLocalDateInput(utcDateStr) {
  if (!utcDateStr) return ''

//   console.log('utcDateStr', utcDateStr) // Debug
  
  const userTimeZone = Intl.DateTimeFormat().resolvedOptions().timeZone
  return moment.utc(utcDateStr).tz(userTimeZone).format('YYYY-MM-DD')
}

// 14 Nov 2025
const convertToUTCDate = (dateStr) => {
  const userTimeZone = moment.tz.guess(); // e.g., 'America/Los_Angeles'
  return moment.tz(dateStr, "YYYY-MM-DD", userTimeZone).toDate(); // returns UTC Date object
};
  

export {
    getNearestMonday,
    calculateFirstWeek,
    calculateLastWeek,
    calculateWeekBoundaries,
    calculatePTDayBoundaries,
    convertLocalDateToUTCISO,   // 01 Sep 2025
    formatUTCToLocalDateInput,  // 01 Sep 2025
    convertToUTCDate, // 14 Nov 2025
}